/**
 * Renders an agent's message text as sanitised HTML — used inside Task Thread
 * and Mission Channel bubbles. The agent emits Tailwind-classed HTML per
 * `partials/communication_style.j2`; we run it through DOMPurify with a
 * conservative allow-list before injecting.
 */

import { memo } from "react";
import { clsx } from "clsx";
import { sanitizeRichHtml } from "@/utils/sanitizeHtml";

interface AgentMessageHtmlProps {
  html: string;
  className?: string;
}

function AgentMessageHtmlInner({ html, className }: AgentMessageHtmlProps) {
  const trimmed = (html ?? "").trim();
  if (!trimmed) {
    return null;
  }

  const safe = sanitizeRichHtml(trimmed);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: `
        .agent-message-wrapper {
          min-width: 0 !important;
          width: 100% !important;
          max-width: 100% !important;
          overflow-wrap: break-word !important;
          word-break: break-word !important;
        }
        .agent-message-wrapper table {
          display: block !important;
          width: 100% !important;
          max-width: 100% !important;
          overflow-x: auto !important;
          -webkit-overflow-scrolling: touch !important;
          border-collapse: collapse !important;
        }
        .agent-message-wrapper div,
        .agent-message-wrapper blockquote,
        .agent-message-wrapper section,
        .agent-message-wrapper article {
          max-width: 100% !important;
          overflow-x: auto !important;
        }
      `}} />
      <div
        className={clsx(
          "agent-message-wrapper agent-message text-sm leading-relaxed min-w-0 w-full max-w-full overflow-hidden text-slate-700 dark:text-slate-200",
          "[&_ul]:list-disc [&_ul]:ml-5 [&_ul]:mb-2",
          "[&_ol]:list-decimal [&_ol]:ml-5 [&_ol]:mb-2",
          "[&_li]:mb-1",
          "[&_p]:mb-2 [&_p:last-child]:mb-0",
          "[&_strong]:font-semibold [&_strong]:text-slate-900 dark:[&_strong]:text-slate-100",
          "[&_a]:text-indigo-600 [&_a]:underline [&_a]:underline-offset-2 dark:[&_a]:text-indigo-300",
          "[&_code]:rounded [&_code]:bg-slate-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-indigo-700 dark:[&_code]:bg-slate-800/60 dark:[&_code]:text-indigo-200",
          className
        )}
        dangerouslySetInnerHTML={{ __html: safe }}
      />
    </>
  );
}

export const AgentMessageHtml = memo(
  AgentMessageHtmlInner,
  (a, b) => a.html === b.html && a.className === b.className
);
AgentMessageHtml.displayName = "AgentMessageHtml";
